import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { all_routes } from "../../../router/all_routes";
import { Teacher } from "../../../../core/data/types/teacher";
import { teacherinputs } from "../../../../core/common/inputoption/inputoption";
import { schoolClassId, classSectionId } from "../../../../core/common/selectoption/selectoption";
import CommonSelect from "../../../../core/common/commonSelect";
import CustomInput from "../../../../core/common/commonInput";
import { addTeacherApi, updateTeacherApi, fetchUserRoleApi } from "../../../../core/data/services/teacherService";
import { addteacherSuccess } from "../../../../core/data/redux/teacherSlice";
import { getMandatoryFields } from "../../../../core/common/commonServices";
import { AppDispatch } from "../../../../core/data/redux/store";
import SuccessModal from "../../../../core/common/successModal";
import ErrorModal from "../../../../core/common/ErrorModal";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { REACT_APP_AWS_REGION, REACT_APP_AWS_ACCESS_KEY_ID, REACT_APP_AWS_SECRET_ACCESS_KEY, REACT_APP_AWS_S3_BUCKET_NAME } from "../../../../constants/constants"
import WebcamCapture from "../../../../core/common/WebcamCapture";
import { getUserDetails } from "../../../../lib/getUserDetails";

type Option = { label: string; value: string };

interface Role {
  id: number;
  name: string;
  isActive: boolean;
  isDeleted: boolean;
  roleKey: string;
  referenceKey: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

// Configure AWS SDK (DANGER: Do not use real credentials in production)
const s3 = new S3Client({
  region: REACT_APP_AWS_REGION || "us-east-1",
  credentials: {
    accessKeyId: REACT_APP_AWS_ACCESS_KEY_ID || "YOUR_AWS_ACCESS_KEY_ID",
    secretAccessKey: REACT_APP_AWS_SECRET_ACCESS_KEY || "YOUR_AWS_SECRET_ACCESS_KEY",
  },
});

async function uploadFileToS3(file: File): Promise<string> {
  // Validate file size (4MB limit)
  const maxSize = 4 * 1024 * 1024; // 4MB in bytes
  if (file.size > maxSize) {
    throw new Error("File size exceeds 4MB limit");
  }

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error("Invalid file type. Only JPG, PNG, and SVG are allowed");
  }

  // Generate unique filename
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 15);
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  const fileName = `${timestamp}_${randomString}.${fileExtension}`;

  try {
    // Convert file to Uint8Array
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    // Create S3 upload command
    const command = new PutObjectCommand({
      Bucket: REACT_APP_AWS_S3_BUCKET_NAME,
      Key: fileName,
      Body: uint8Array,
      ContentType: file.type,
      // Uncomment if you want public access
      // ACL: "public-read",
    });

    // Upload to S3
    const result = await s3.send(command);
    console.log("S3 upload successful:", result);

    // Return the S3 URL
    const s3Url = `https://${REACT_APP_AWS_S3_BUCKET_NAME}.s3.${REACT_APP_AWS_REGION}.amazonaws.com/${fileName}`;
    return fileName;
  } catch (error) {
    console.error("S3 upload error:", error);

    // Provide more specific error messages
    if (error instanceof Error) {
      if (error.message.includes('AccessDenied')) {
        throw new Error("Access denied. Check your AWS credentials and permissions.");
      } else if (error.message.includes('NoSuchBucket')) {
        throw new Error("S3 bucket not found. Check your bucket name.");
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }

    throw new Error("Unknown error occurred during upload");
  }
}

// Custom validation function
const validateTeacherForm = (formData: Omit<Teacher, "id">, mandatoryFields: string[]) => {
  const errors: { [key: string]: string } = {};

  // Check mandatory fields
  mandatoryFields.forEach((field) => {
    if (!formData[field as keyof typeof formData] ||
      String(formData[field as keyof typeof formData]).trim() === "") {
      errors[field] = `${field} is required`;
    }
  });

  // First name validation - at least 3 characters
  if (formData.firstName && formData.firstName.trim().length < 3) {
    errors.firstName = "First name must be at least 3 characters long";
  }

  // Phone number validation - exactly 10 digits
  if (formData.phone) {
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone)) {
      errors.phone = "Phone number must be exactly 10 digits";
    }
  }

  // Aadhar number validation - exactly 12 digits
  if (formData.aadharNumber) {
    const aadharRegex = /^\d{12}$/;
    if (!aadharRegex.test(formData.aadharNumber)) {
      errors.aadharNumber = "Aadhar number must be exactly 12 digits";
    }
  }

  // Email validation
  if (formData.email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
  }

  return errors;
};

const getInitialFormState = (schoolId: number): Omit<Teacher, "id"> => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  profileImage: "",
  aadharNumber: "",
  designation: "",
  schoolClassId: "",
  classSectionId: "",
  roleIds: [8],
  parameters: [schoolId],
});

const TeacherForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { state } = useLocation();
  const teacherFromState = state?.teacher as Teacher | undefined;
  const isEdit = Boolean(teacherFromState);

  const userData = getUserDetails();
  const schoolId = userData?.[0]?.relatedEntity?.schools?.id;

  const [formData, setFormData] = useState<Omit<Teacher, "id">>(getInitialFormState(schoolId));
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [roles, setRoles] = useState<Role[]>([]);
  const [, setLoadingRoles] = useState<boolean>(false);
  const [imageUploading, setImageUploading] = useState<boolean>(false);
  const [showCaptureModal, setShowCaptureModal] = useState(false);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");

  // Error modal state
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Loading state
  const [loading, setLoading] = useState<boolean>(false);

  // Compute mandatory fields once
  const mandatoryFields = getMandatoryFields(teacherinputs);

  // Create dynamic options map
  const optionsMap: Record<string, Option[]> = {
    designation: roles.map(role => ({
      label: role.name,
      value: role.name // Store name as value for designation field
    })),
    schoolClassId,
    classSectionId
  };

  // Fetch roles on component mount
  useEffect(() => {
    const fetchRoles = async () => {
      setLoadingRoles(true);
      try {
        const response = await fetchUserRoleApi();
        if (response.data && Array.isArray(response.data)) {
          setRoles(response.data);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
        setErrorMessage("Failed to load roles. Please try again.");
        setShowErrorModal(true);
      } finally {
        setLoadingRoles(false);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    if (isEdit && teacherFromState) {
      const { id, ...rest } = teacherFromState;
      setFormData(rest);
    }
  }, [isEdit, teacherFromState]);

  const handleInputChange = (key: keyof Omit<Teacher, "id">, value: any) => {

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));

    // Remove inline error if present
    setErrors((prev) => {
      if (prev[key as string]) {
        const updated = { ...prev };
        delete updated[key as string];
        return updated;
      }
      return prev;
    });
  };


  const handleSelectChange = (key: keyof Omit<Teacher, "id">) => (selected: any) => {
    const selectedValue = selected?.value || "";

    // Special handling for designation
    if (key === "designation") {
      // Find the selected role to get its ID
      const selectedRole = roles.find(role => role.name === selectedValue);
      if (selectedRole) {
        // Update both designation (with name) and roleIds (with ID)
        setFormData(prev => ({
          ...prev,
          designation: selectedValue,
          roleIds: [selectedRole.id]
        }));
      } else {
        handleInputChange(key, selectedValue);
      }
    } else {
      handleInputChange(key, selectedValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Reset modals/messages
    setShowErrorModal(false);
    setErrorMessage("");
    setShowSuccessModal(false);
    setSuccessMessage("");

    // Validate using custom validation
    const newErrors = validateTeacherForm(formData, mandatoryFields);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setLoading(true);
    try {
      if (isEdit && teacherFromState) {
        // Build changedFields
        const changedFields: Partial<Teacher> & { id: number } = {
          id: teacherFromState.id as number,
        };
        let anyChange = false;
        Object.keys(formData).forEach((key) => {
          const k = key as keyof Omit<Teacher, "id">;
          if (formData[k] !== (teacherFromState as any)[k]) {
            (changedFields as any)[k] = formData[k];
            anyChange = true;
          }
        });
        if (!anyChange) {
          setSuccessMessage("No changes detected.");
          setShowSuccessModal(true);
        } else {
          const updatedResp = await updateTeacherApi(changedFields as Teacher);
          const updatedTeacher: Teacher =
            (updatedResp as any).data ? (updatedResp as any).data : (updatedResp as Teacher);
          dispatch(addteacherSuccess(updatedTeacher));
          setSuccessMessage("Successfully updated!");
          setShowSuccessModal(true);
          navigate(all_routes.teacherGrid);
        }
      } else {
        const addedResp = await addTeacherApi(formData);
        const addedTeacher: Teacher =
          (addedResp as any).data ? (addedResp as any).data : (addedResp as Teacher);
        dispatch(addteacherSuccess(addedTeacher));
        setSuccessMessage("Successfully added!");
        setShowSuccessModal(true);
        navigate(all_routes.teacherGrid);
        setFormData(getInitialFormState(schoolId));
      }
      // Clear inline errors
      setErrors({});
    } catch (err: any) {
      console.error("Error submitting form:", err);

      let errorMessage = "Something went wrong. Please try again.";

      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.name === 'ApiError' && err.message) {
        errorMessage = err.message;
      } else if (err?.message && err.message !== "Request failed with status code 400") {
        errorMessage = err.message;
      }

      console.log("Final error message:", errorMessage);
      setErrorMessage(errorMessage);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage("");
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setSuccessMessage("");
  };



  return (
    <>
      <div className="page-wrapper">
        <div className="content content-two">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">{isEdit ? "Edit" : "Add"} Teacher</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.HeadmasterDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.teacherList}>Teacher</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit" : "Add"} Teacher
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <form onSubmit={handleSubmit}>
                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h4 className="text-dark">{isEdit ? "Edit" : "Add"} Teacher</h4>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row row-cols-xxl-5 row-cols-md-6">
                      {teacherinputs.map(({ label, type, key, pattern, required, maxLength, errorMessage }: any) => (
                        <div className="col-xxl col-xl-3 col-md-6" key={key}>
                          <div className="mb-3">
                            <label className="form-label">
                              {label}
                              {required && <span style={{ color: "red" }}> *</span>}
                            </label>
                            {key === "faceImage" ? (
                              <>
                                <div className="profile-upload">
                                  {/* Display current image if exists */}
                                  {formData.faceImage && (
                                    <img
                                      src={formData.faceImage}
                                      alt="Face Preview"
                                      style={{ width: 100, height: 100, objectFit: "cover", marginBottom: 10 }}
                                    />
                                  )}

                                  {imageUploading && <p>Uploading...</p>}

                                  {/* File Upload Option */}
                                  <div className="upload-option mb-3">
                                    <label htmlFor="faceImageUpload" className="btn btn-primary me-2">
                                      <i className="fas fa-upload me-2"></i>
                                      {/* Upload Image */}
                                    </label>
                                    <input
                                      id="faceImageUpload"
                                      type="file"
                                      accept="image/jpeg,image/jpg"
                                      style={{ display: 'none' }}
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          // Validate file size (4MB limit)
                                          if (file.size > 4 * 1024 * 1024) {
                                            setErrorMessage("File size must be under 4MB");
                                            setShowErrorModal(true);
                                            return;
                                          }

                                          // Validate file type
                                          if (!file.type.includes('jpeg') && !file.type.includes('jpg')) {
                                            setErrorMessage("Only JPG format is allowed");
                                            setShowErrorModal(true);
                                            return;
                                          }

                                          try {
                                            setImageUploading(true);
                                            const s3Url = await uploadFileToS3(file);
                                            handleInputChange("faceImage", s3Url);
                                          } catch (err: any) {
                                            console.error("Face image upload failed:", err);
                                            setErrorMessage(`Face image upload failed: ${err.message}`);
                                            setShowErrorModal(true);
                                          } finally {
                                            setImageUploading(false);
                                          }
                                        }
                                      }}
                                    />

                                    {/* Camera Capture Button */}
                                    <button
                                      type="button"
                                      className="btn btn-success"
                                      onClick={() => setShowCaptureModal(true)}
                                    >
                                      <i className="fas fa-camera me-2"></i>
                                      {/* Capture Image */}
                                    </button>
                                    {/* Remove Button */}
                                  </div>




                                  <p className="fs-12 mt-2 text-muted">
                                    Upload or capture image • Size under 4MB • JPG format only
                                  </p>
                                </div>

                                {/* Camera Capture Modal */}
                                {showCaptureModal && (
                                  <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                                    <div className="modal-dialog modal-lg modal-dialog-centered">
                                      <div className="modal-content">
                                        <div className="modal-header">
                                          <h5 className="modal-title">
                                            <i className="fas fa-camera me-2"></i>
                                            Capture Face Image
                                          </h5>
                                          <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setShowCaptureModal(false)}
                                          ></button>
                                        </div>
                                        <div className="modal-body text-center">
                                          <WebcamCapture
                                            onCapture={async (file) => {
                                              try {
                                                setImageUploading(true);
                                                setShowCaptureModal(false); // Close modal immediately after capture
                                                const s3Url = await uploadFileToS3(file);
                                                handleInputChange("faceImage", s3Url);
                                              } catch (err: any) {
                                                console.error("Face image upload failed:", err);
                                                setErrorMessage(`Face image upload failed: ${err.message}`);
                                                setShowErrorModal(true);
                                              } finally {
                                                setImageUploading(false);
                                              }
                                            }}
                                          />
                                          <p className="text-muted mt-3 mb-0">
                                            Position your face in the camera frame and click capture
                                          </p>
                                        </div>
                                        <div className="modal-footer">
                                          <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setShowCaptureModal(false)}
                                          >
                                            Cancel
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : type === "select" ? (
                              <CommonSelect
                                className="select"
                                options={optionsMap[key] || []}
                                defaultValue={
                                  isEdit &&
                                    formData[key] !== null &&
                                    formData[key] !== ""
                                    ? { value: String(formData[key]), label: String(formData[key]) }
                                    : undefined
                                }
                                onChange={handleSelectChange(
                                  key as keyof Omit<Teacher, "id">
                                )}
                              // isLoading={key === "designation" && loadingRoles}
                              />
                            ) : (
                              <CustomInput
                                id={key}
                                className="form-control"
                                type={type}
                                placeholder={label}
                                value={String(formData[key as keyof typeof formData] || "")}
                                onChange={(e) =>
                                  handleInputChange(key as keyof Omit<Teacher, "id">, e.target.value)
                                }
                                pattern={pattern}
                                maxLength={maxLength}
                                required={required}
                                errorMessage={errorMessage}
                                label={label}
                              />
                            )}
                            {errors[key] && (
                              <div className="text-danger">{errors[key]}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="text-end">
                  <button
                    type="button"
                    className="btn btn-light me-3"
                    onClick={() => navigate(all_routes.teacherGrid)}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading
                      ? isEdit
                        ? "Saving..."
                        : "Adding..."
                      : isEdit
                        ? "Save Changes"
                        : "Add Teacher"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onClose={handleCloseSuccessModal}
        message={successMessage}
      />

      {/* Error Modal */}
      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={handleCloseErrorModal}
      />
    </>
  );
};

export default TeacherForm;